import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { INSTAGRAM_URL, CONTACT_EMAIL, LOGO } from "../../lib/media";
import { useI18n } from "../../i18n/LanguageContext";
import { routePath } from "../../lib/routes";
import { SITE } from "../../content/site";
import { micro } from "../../lib/microcopy";
import { track, EVENTS } from "../../lib/analytics";

const FLink = ({ to, children, testid }) => (
  <li>
    <Link to={to} data-testid={testid} className="text-sm font-light text-[#4a4a4a] transition-colors hover:text-[#0e7c6b]">
      {children}
    </Link>
  </li>
);

export default function Footer() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const c = SITE[lang];

  return (
    <footer className="border-t border-black/8 bg-white px-6 pt-24 pb-28 md:px-12">
      <div className="mx-auto max-w-[1400px]">
        <button
          data-testid="footer-book-btn"
          onClick={() => { track(EVENTS.CTA_CLICK, { cta: "footer_book" }); navigate(routePath("book", lang)); }}
          className="font-display block text-left text-4xl font-light leading-[1.02] tracking-tightest text-[#050505] transition-colors hover:text-[#0e7c6b] md:text-6xl"
        >
          {t("footer.book")}
          <ArrowUpRight className="ml-2 inline-block" size={36} />
        </button>

        <div className="mt-20 grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-5 md:gap-8">
          <div className="col-span-2 md:col-span-1">
            <img src={LOGO.horizontalBlack} alt="Equestrian Partners" className="h-6 w-auto shrink-0 object-contain" loading="lazy" />
            <p className="mt-4 max-w-xs text-sm font-light leading-relaxed text-[#4a4a4a]">
              {t("footer.tagline")}
            </p>
            <p className="mt-4 text-xs font-light leading-relaxed text-[#9a9a9a]">
              Equestrian Partners, Inc.<br />131 Continental Dr, Suite 305<br />Newark, DE 19713, USA
            </p>
          </div>

          <nav aria-label="Company" className="min-w-0">
            <p className="overline mb-5 text-[#9a9a9a]">{micro(lang, "company")}</p>
            <ul className="space-y-3">
              <FLink to={routePath("about", lang)} testid="footer-nav-about">{c.nav.about}</FLink>
              <FLink to={routePath("industries", lang)} testid="footer-nav-industries">{c.nav.industries}</FLink>
              <FLink to={routePath("faq", lang)} testid="footer-nav-faq">FAQ</FLink>
              <FLink to={routePath("contact", lang)} testid="footer-nav-contact">{c.nav.contact}</FLink>
              <FLink to={routePath("book", lang)} testid="footer-nav-session">{c.nav.book}</FLink>
            </ul>
          </nav>

          <nav aria-label="Services" className="min-w-0">
            <p className="overline mb-5 text-[#9a9a9a]">{c.nav.services}</p>
            <ul className="space-y-3">
              <li>
                <Link to={routePath("events", lang)} data-testid="footer-nav-events"
                  className="text-sm font-light text-[#4a4a4a] transition-colors hover:text-[#0e7c6b]">
                  {c.nav.events}
                </Link>
              </li>
              <FLink to={routePath("eventsCapabilities", lang)} testid="footer-nav-capabilities">{c.nav.capabilities || SITE.en.nav.capabilities}</FLink>
              <FLink to={routePath("ventures", lang)} testid="footer-nav-ventures">{c.nav.ventures || SITE.en.nav.ventures}</FLink>
              <FLink to={routePath("technology", lang)} testid="footer-nav-technology">{c.nav.technology || SITE.en.nav.technology}</FLink>
              <FLink to={routePath("work", lang)} testid="footer-nav-work">{c.nav.work || SITE.en.nav.work}</FLink>
              {c.services.sections.map((s) => (
                <li key={s.id}>
                  <Link to={`${routePath("services", lang)}#${s.id}`} data-testid={`footer-service-${s.id}`}
                    className="text-sm font-light text-[#4a4a4a] transition-colors hover:text-[#0e7c6b]">
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Products" className="min-w-0">
            <p className="overline mb-5 text-[#9a9a9a]">{c.nav.products}</p>
            <ul className="space-y-3">
              <FLink to={routePath("productArena", lang)} testid="footer-product-arena">Arena</FLink>
              <FLink to={routePath("productCentauros", lang)} testid="footer-product-centauros">Centauros</FLink>
              <FLink to={routePath("productAos", lang)} testid="footer-product-aos">AOS Equestrian</FLink>
              <FLink to={routePath("products", lang)} testid="footer-products-all">{micro(lang, "allProducts")}</FLink>
            </ul>
          </nav>

          <div className="min-w-0">
            <p className="overline mb-5 text-[#9a9a9a]">{t("footer.contactTitle")}</p>
            <ul className="space-y-3">
              <FLink to={routePath("blog", lang)} testid="footer-nav-blog">{c.nav.blog}</FLink>
              <li>
                <a data-testid="footer-contact-link" href={`mailto:${CONTACT_EMAIL}`}
                  className="block break-all text-sm font-light text-[#4a4a4a] transition-colors hover:text-[#0e7c6b]">
                  {CONTACT_EMAIL}
                </a>
              </li>
              <li>
                <a data-testid="footer-instagram-link" href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer"
                  onClick={() => track(EVENTS.OUTBOUND_LINK_CLICKED, { url: INSTAGRAM_URL })}
                  className="text-sm font-light text-[#4a4a4a] transition-colors hover:text-[#0e7c6b]">
                  Instagram
                </a>
              </li>
            </ul>
            <p className="overline mb-4 mt-8 text-[#9a9a9a]">{t("footer.legalTitle")}</p>
            <ul className="space-y-3">
              <FLink to={routePath("privacy", lang)} testid="footer-legal-privacidad">{t("footer.legal.privacy")}</FLink>
              <FLink to={routePath("cookies", lang)} testid="footer-legal-cookies">{t("footer.legal.cookies")}</FLink>
              <FLink to={routePath("terms", lang)} testid="footer-legal-terminos">{t("footer.legal.terms")}</FLink>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-3 border-t border-black/8 pt-8">
          <button
            data-testid="footer-cookie-settings"
            onClick={() => window.dispatchEvent(new CustomEvent("open-cookie-settings"))}
            className="w-fit text-xs font-light text-[#9a9a9a] transition-colors hover:text-[#0e7c6b]"
          >
            {t("footer.cookiePrefs")}
          </button>
          <span className="text-xs font-light text-[#9a9a9a]">
            © {new Date().getFullYear()} Equestrian Partners, Inc. {t("footer.rights")}
          </span>
        </div>
      </div>
    </footer>
  );
}
